import type { ApprovalCall } from "@cleo/contracts";

import { COMPONENT_RELEASE_ACTION } from "./neucleos/componentReleaseCard";
import {
  APPLY_MEMBER_CHANGE_TOOL,
  calledTool,
  listGateInstances,
  SUBSCRIPTION_CANCEL_GATE_KEY,
  type GateDecisionOutcome,
  type GateInstanceRow,
} from "./neucleos/gateInstancesClient";

// One-key answers in the agent strip's Inbox popover (agent-strip phase 3,
// 7 October 2026). The person opens a decision's card, presses 1 to approve
// or 2 to decline, and has two seconds to take it back with Esc. These are
// the pure halves of the six rules the Orchestrator set; AgentStrip.tsx holds
// the timer and sends only through the card's one decision path
// (decideGateCard).

/** How long an answer by key waits on the client before anything is sent. */
export const ANSWER_HOLD_MS = 2_000;

export type StripKeyAction =
  | { readonly kind: "answer"; readonly outcome: GateDecisionOutcome }
  | { readonly kind: "move"; readonly delta: 1 | -1 }
  | { readonly kind: "undo" }
  /** E: mark the focused Finished card read (#1373). */
  | { readonly kind: "clear" };

export interface StripKeyEvent {
  readonly key: string;
  readonly repeat: boolean;
  readonly altKey: boolean;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly shiftKey: boolean;
  readonly target: EventTarget | null;
}

/** Whether a key went to something the person types into: it is theirs, never an answer. */
function isTypingTarget(target: EventTarget | null): boolean {
  if (typeof Element === "undefined" || !(target instanceof Element)) return false;
  if (target.closest("input, textarea, select, [contenteditable=''], [contenteditable='true']"))
    return true;
  return target instanceof HTMLElement && target.isContentEditable;
}

/**
 * Rule 2 and the held-key rule: the action a key asks for, or null. A held
 * (repeating) key, a key with a modifier, or a key typed into a field is never
 * an answer. 1 approves and 2 declines: these cards have no other option, so
 * no key can choose one that widens a permission (rule 5).
 */
export function stripKeyAction(event: StripKeyEvent): StripKeyAction | null {
  if (event.repeat || event.altKey || event.ctrlKey || event.metaKey) return null;
  if (isTypingTarget(event.target)) return null;
  if (event.key === "Escape") return { kind: "undo" };
  if (event.shiftKey) return null;
  switch (event.key) {
    case "1":
      return { kind: "answer", outcome: "approved" };
    case "2":
      return { kind: "answer", outcome: "declined" };
    case "e":
    case "E":
      return { kind: "clear" };
    case "j":
    case "J":
      return { kind: "move", delta: 1 };
    case "k":
    case "K":
      return { kind: "move", delta: -1 };
    default:
      return null;
  }
}

/**
 * Rule 6: whether keys may answer this card. A gate approved only with the
 * person's Touch ID (the World's PRESENCE_APPROVED_GATE_KEYS) never takes a
 * key, and neither does a card whose own Approve the card keeps unavailable
 * (blocked words, a missing request) or a member change, which is approved
 * only on its full card. Such a card says "Open to approve".
 */
export function keysMayAnswer(
  row: Pick<GateInstanceRow, "gateKey" | "memberChangeKey" | "request">,
  approveAvailable: boolean,
): boolean {
  if (row.gateKey === SUBSCRIPTION_CANCEL_GATE_KEY) return false;
  // A member change is known by its gate and its call as well as its key, so
  // a row that arrives without the key still takes none (#1365 round 1).
  if (row.memberChangeKey || row.gateKey === MEMBER_CHANGE_GATE_KEY) return false;
  if (calledTool(row.request?.call) === APPLY_MEMBER_CHANGE_TOOL) return false;
  // #1844: a platform Components release is approved only by its card's own
  // button, whose final re-read checks the release; a key never answers it.
  if (isComponentReleaseCall(row.request?.call)) return false;
  return approveAvailable;
}

function isComponentReleaseCall(call: ApprovalCall | null | undefined): boolean {
  if (calledTool(call) !== "neucleos_canvas" || call?.kind !== "tool") return false;
  const args = call.args;
  return (
    typeof args === "object" &&
    args !== null &&
    (args as Record<string, unknown>)["action"] === COMPONENT_RELEASE_ACTION
  );
}

/** The World's gate for a change to its members (worldMemberTools.js). */
const MEMBER_CHANGE_GATE_KEY = "HG-WORLD-GOVERN-02";

export const OPEN_TO_APPROVE = "Open to approve: this card has no key.";
/** A card Cleo was stopped on has no Approve at all, so it says why it has no key (#1373). */
export const STOPPED_NO_KEY = "Only Decline is left on this card: it has no key.";

export type GateReread =
  | { readonly ok: true; readonly row: GateInstanceRow }
  | { readonly ok: false; readonly reason: string };

const REREAD_MAX_PAGES = 6;

/**
 * Rule 4: the card is read again from the World just before an answer by
 * key is sent. If the instance is gone, decided, cancelled, or its digest
 * changed (a card replaced underneath), nothing is sent and the reason is
 * shown. The World also refuses a stale digest itself (GATE_DIGEST_MISMATCH).
 */
export async function rereadGateForAnswer(
  worldKey: string,
  expected: Pick<GateInstanceRow, "gateInstanceKey" | "targetDigest">,
  list: typeof listGateInstances = listGateInstances,
): Promise<GateReread> {
  let cursor: string | null = null;
  for (let page = 0; page < REREAD_MAX_PAGES; page++) {
    const outcome = await list(worldKey, "CREATED", fetch, cursor);
    if (!outcome.ok) return { ok: false, reason: outcome.reason };
    const row = outcome.rows.find((item) => item.gateInstanceKey === expected.gateInstanceKey);
    if (row) {
      if (row.state !== "CREATED" || !row.humanDecides || row.effectStatus === "cancelled")
        return { ok: false, reason: "This request is no longer waiting for your decision." };
      if (row.targetDigest !== expected.targetDigest)
        return {
          ok: false,
          reason: "This request changed since you opened it. Nothing was sent; read it again.",
        };
      return { ok: true, row };
    }
    cursor = outcome.nextCursor ?? null;
    if (!cursor) break;
  }
  return { ok: false, reason: "This request is no longer waiting for your decision." };
}
